import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import {
  getDefaultAdminRoute,
  getUserRoleCode,
  loginUser,
  ROLE_CODES,
} from "../../admin/utils/auth";
import {
  isCityLocation,
  listLocations,
  resolveLocationCountry,
  signupBuyer,
} from "../../api/authApi";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.trivomtara.com/api"
).replace(/\/$/, "");

const EMPTY_SIGNUP = {
  firstName: "",
  lastName: "",
  gender: "",
  age: "",
  locationId: "",
  countryCode: "+91",
  phone: "",
  password: "",
  confirmPassword: "",
};

const GENDERS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const inputCustomStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "gray" },
    "&:hover fieldset": { borderColor: "#000000" },
    "&.Mui-focused fieldset": { borderColor: "#cda202" },
  },
  "& .MuiInputLabel-root": { color: "gray" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#cda202" },
};

const primaryBtnSx = {
  fontFamily: "Lora",
  bgcolor: "#CDA202",
  padding: "12px",
  "&:hover": { bgcolor: "#b89202" },
};

const secondaryBtnSx = {
  fontFamily: "Lora",
  color: "#CDA202",
  borderColor: "#CDA202",
  padding: "12px",
  "&:hover": {
    borderColor: "#b89202",
    bgcolor: "#faf6e6",
  },
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | signup | password-change

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [password, setPassword] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [signup, setSignup] = useState(EMPTY_SIGNUP);
  const [signupErrors, setSignupErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);

  const backFun = () => {
    if (mode === "signup") {
      setMode("login");
      return;
    }
    navigate(-1);
  };

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (location.state?.message) {
      showToast(location.state.message, "info");
    }
  }, [location.state?.message]);

  useEffect(() => {
    if (mode !== "signup") return undefined;
    let cancelled = false;
    setLoadingLocations(true);
    listLocations()
      .then((rows) => {
        if (!cancelled) setLocations(rows || []);
      })
      .catch((error) => {
        if (!cancelled) {
          showToast(error.message || "Failed to load cities", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLocations(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    return () => {
      if (profilePreview) URL.revokeObjectURL(profilePreview);
    };
  }, [profilePreview]);

  const cityOptions = useMemo(() => {
    const cities = locations.filter(isCityLocation);
    const source = cities.length ? cities : locations;
    return [...source]
      .sort((a, b) =>
        String(a.location_name || "").localeCompare(String(b.location_name || ""))
      )
      .map((location) => ({
        value: String(location.location_id),
        label: location.location_name,
      }));
  }, [locations]);

  const updateSignup = (key, value) => {
    setSignup((prev) => ({ ...prev, [key]: value }));
    setSignupErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleLocationSelect = (locationId) => {
    const resolved = resolveLocationCountry(locations, locationId);
    setSignup((prev) => ({
      ...prev,
      locationId,
      countryCode: resolved.country_code || prev.countryCode || "+91",
    }));
    setSignupErrors((prev) => ({ ...prev, locationId: undefined }));
  };

  const handleProfilePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (profilePreview) URL.revokeObjectURL(profilePreview);
    setProfilePicture(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const finishAuthRedirect = () => {
    const role = getUserRoleCode();
    const portalRoles = [
      ROLE_CODES.ADMIN,
      ROLE_CODES.SUPPLIER,
      ROLE_CODES.SERVICE_PROVIDER,
      ROLE_CODES.VERIFIER,
      ROLE_CODES.LOCATION_ADMIN_SUPPLY,
      ROLE_CODES.LOCATION_ADMIN_DEMAND,
    ];
    if (portalRoles.includes(role)) {
      navigate(getDefaultAdminRoute());
      return;
    }
    const redirectTo =
      location.state?.from && location.state.from !== "/login"
        ? location.state.from
        : "/";
    navigate(redirectTo);
  };

  const handleLogin = async () => {
    const phone = phoneNumber.trim();
    const pwd = password.trim();
    if (!phone) {
      showToast("Please enter a valid phone number", "error");
      return;
    }
    if (!pwd) {
      showToast("Please enter your password", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, password: pwd }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Invalid credentials");
      }

      if (data.data.requires_password_change) {
        setPendingUser(data.data);
        setMode("password-change");
        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        showToast("Please create a new password to continue.", "info");
        return;
      }

      loginUser(data.data);
      showToast("Login successful", "success");
      setTimeout(finishAuthRedirect, 600);
    } catch (error) {
      showToast(error.message || "Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword.length < 8) {
      showToast("New password must be at least 8 characters", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("New password and confirmation do not match", "error");
      return;
    }
    if (newPassword === password) {
      showToast(
        "New password must be different from the temporary password",
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: pendingUser.phone,
          current_password: password,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update password");
      }

      setPendingUser(null);
      setMode("login");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      showToast(
        "Password updated successfully. Log in with your new password.",
        "success"
      );
    } catch (error) {
      showToast(error.message || "Failed to update password", "error");
    } finally {
      setLoading(false);
    }
  };

  const validateSignup = () => {
    const next = {};
    if (signup.firstName.trim().length < 2) {
      next.firstName = "Enter a valid first name";
    }
    if (!signup.lastName.trim()) {
      next.lastName = "Last name is required";
    }
    if (!signup.gender) {
      next.gender = "Select gender";
    }
    const ageNum = Number(signup.age);
    if (!signup.age || Number.isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      next.age = "Enter a valid age";
    }
    if (!signup.locationId) {
      next.locationId = "Select a city";
    }
    const phone = signup.phone.trim();
    if (!phone) {
      next.phone = "Phone number is required";
    } else if (
      signup.countryCode === "+91"
        ? !/^[6-9]\d{9}$/.test(phone)
        : phone.length < 7
    ) {
      next.phone = "Enter a valid phone number";
    }
    if (signup.password.length < 8) {
      next.password = "Password must be at least 8 characters";
    }
    if (signup.password !== signup.confirmPassword) {
      next.confirmPassword = "Passwords do not match";
    }
    return next;
  };

  const handleSignup = async () => {
    const nextErrors = validateSignup();
    setSignupErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      const { login } = await signupBuyer({
        firstName: signup.firstName,
        lastName: signup.lastName,
        gender: signup.gender,
        age: signup.age,
        locationId: Number(signup.locationId),
        countryCode: signup.countryCode || "+91",
        phoneNumbers: [signup.phone],
        password: signup.password,
        profilePicture,
      });

      loginUser(login);
      showToast("Account created successfully", "success");
      setTimeout(() => navigate("/"), 600);
    } catch (error) {
      showToast(error.message || "Sign up failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const openSignup = () => {
    setMode("signup");
    setSignup(EMPTY_SIGNUP);
    setSignupErrors({});
    setProfilePicture(null);
    if (profilePreview) URL.revokeObjectURL(profilePreview);
    setProfilePreview("");
    setShowSignupPassword(false);
    setShowSignupConfirm(false);
  };

  const headerTitle =
    mode === "signup"
      ? "Create Account"
      : mode === "password-change"
        ? "Update Password"
        : "Continue with Phone";

  const subtitle =
    mode === "signup"
      ? "Create your buyer account to continue"
      : mode === "password-change"
        ? "Create a new password before continuing"
        : "Enter your mobile number and password to login";

  return (
    <div className="flex items-center justify-center pt-5 p-4">
      <div className="bg-white rounded-lg border w-full max-w-xl overflow-hidden">
        <div className="bg-[#CDA202] text-white px-6 py-2 flex items-center justify-between">
          <h2 className="text-lg font-medium mb-0 text-lora">{headerTitle}</h2>
          <IconButton className="close-btn" onClick={backFun}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="p-6">
          <p className="text-gray-800 font-medium mb-6">{subtitle}</p>

          {mode === "login" && (
            <>
              <div className="mb-2">
                <div className="flex rounded-md overflow-hidden">
                  <TextField
                    fullWidth
                    label="Enter Your Phone Number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setPhoneNumber(value);
                    }}
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <select
                          style={{ marginRight: "8px" }}
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                        >
                          <option value="+91">+91</option>
                          <option value="+1">+1</option>
                          <option value="+44">+44</option>
                          <option value="+86">+86</option>
                          <option value="+33">+33</option>
                          <option value="+49">+49</option>
                          <option value="+81">+81</option>
                        </select>
                      ),
                    }}
                    sx={inputCustomStyle}
                  />
                </div>
              </div>

              <div className="mb-6">
                <TextField
                  fullWidth
                  type="password"
                  label="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleLogin();
                  }}
                  sx={inputCustomStyle}
                />
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleLogin}
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={primaryBtnSx}
                >
                  {loading ? "Logging in..." : "LOGIN"}
                </Button>
                <Button
                  onClick={openSignup}
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  sx={secondaryBtnSx}
                >
                  Sign Up
                </Button>
              </div>
            </>
          )}

          {mode === "password-change" && (
            <div className="mb-4">
              <TextField
                fullWidth
                type={showNewPassword ? "text" : "password"}
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Use at least 8 characters"
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showNewPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputCustomStyle}
              />
              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handlePasswordUpdate();
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputCustomStyle}
              />
              <Button
                onClick={handlePasswordUpdate}
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ ...primaryBtnSx, mt: 2 }}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </div>
          )}

          {mode === "signup" && (
            <div className="mb-2">
              <div className="flex flex-col items-center mb-4">
                <label
                  htmlFor="signup-profile"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div
                    className="rounded-full overflow-hidden flex items-center justify-center"
                    style={{
                      width: 88,
                      height: 88,
                      backgroundColor: "#937813",
                    }}
                  >
                    {profilePreview ? (
                      <img
                        src={profilePreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <PersonOutlineIcon sx={{ color: "#fff", fontSize: 40 }} />
                    )}
                  </div>
                  <span className="text-sm mt-2" style={{ color: "#937813" }}>
                    {profilePicture ? "Change Photo" : "Add Photo"}
                  </span>
                </label>
                <input
                  id="signup-profile"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleProfilePick}
                />
              </div>

              <div className="row g-2">
                <div className="col-6">
                  <TextField
                    fullWidth
                    label="First Name"
                    value={signup.firstName}
                    onChange={(e) =>
                      updateSignup(
                        "firstName",
                        e.target.value.replace(/[^a-zA-Z\s]/g, "")
                      )
                    }
                    error={Boolean(signupErrors.firstName)}
                    helperText={signupErrors.firstName}
                    margin="normal"
                    sx={inputCustomStyle}
                  />
                </div>
                <div className="col-6">
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={signup.lastName}
                    onChange={(e) =>
                      updateSignup(
                        "lastName",
                        e.target.value.replace(/[^a-zA-Z\s]/g, "")
                      )
                    }
                    error={Boolean(signupErrors.lastName)}
                    helperText={signupErrors.lastName}
                    margin="normal"
                    sx={inputCustomStyle}
                  />
                </div>
                <div className="col-6">
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    value={signup.gender}
                    onChange={(e) => updateSignup("gender", e.target.value)}
                    error={Boolean(signupErrors.gender)}
                    helperText={signupErrors.gender}
                    margin="normal"
                    sx={inputCustomStyle}
                  >
                    {GENDERS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="col-6">
                  <TextField
                    fullWidth
                    label="Age"
                    value={signup.age}
                    onChange={(e) =>
                      updateSignup(
                        "age",
                        e.target.value.replace(/\D/g, "").slice(0, 3)
                      )
                    }
                    error={Boolean(signupErrors.age)}
                    helperText={signupErrors.age}
                    margin="normal"
                    sx={inputCustomStyle}
                  />
                </div>
                <div className="col-6">
                  <TextField
                    select
                    fullWidth
                    label="City"
                    value={signup.locationId}
                    onChange={(e) => handleLocationSelect(e.target.value)}
                    error={Boolean(signupErrors.locationId)}
                    helperText={
                      signupErrors.locationId ||
                      (loadingLocations ? "Loading cities..." : "")
                    }
                    margin="normal"
                    disabled={loadingLocations || cityOptions.length === 0}
                    sx={inputCustomStyle}
                  >
                    {cityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="col-6">
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={signup.phone}
                    onChange={(e) =>
                      updateSignup(
                        "phone",
                        e.target.value.replace(/\D/g, "").slice(0, 15)
                      )
                    }
                    error={Boolean(signupErrors.phone)}
                    helperText={signupErrors.phone}
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {signup.countryCode || "+91"}
                        </InputAdornment>
                      ),
                    }}
                    sx={inputCustomStyle}
                  />
                </div>
                <div className="col-6">
                  <TextField
                    fullWidth
                    type={showSignupPassword ? "text" : "password"}
                    label="Password"
                    value={signup.password}
                    onChange={(e) => updateSignup("password", e.target.value)}
                    error={Boolean(signupErrors.password)}
                    helperText={
                      signupErrors.password || "Use at least 8 characters"
                    }
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowSignupPassword((prev) => !prev)
                            }
                            edge="end"
                          >
                            {showSignupPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputCustomStyle}
                  />
                </div>
                <div className="col-6">
                  <TextField
                    fullWidth
                    type={showSignupConfirm ? "text" : "password"}
                    label="Confirm Password"
                    value={signup.confirmPassword}
                    onChange={(e) =>
                      updateSignup("confirmPassword", e.target.value)
                    }
                    error={Boolean(signupErrors.confirmPassword)}
                    helperText={signupErrors.confirmPassword}
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowSignupConfirm((prev) => !prev)
                            }
                            edge="end"
                          >
                            {showSignupConfirm ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputCustomStyle}
                  />
                </div>
              </div>

              <Button
                onClick={handleSignup}
                fullWidth
                variant="contained"
                disabled={loading || loadingLocations}
                sx={{ ...primaryBtnSx, mt: 2 }}
              >
                {loading ? "Creating account..." : "Sign Up"}
              </Button>

              <button
                type="button"
                className="w-full text-center mt-4 text-sm bg-transparent border-0 cursor-pointer"
                onClick={() => setMode("login")}
              >
                Already have an account?{" "}
                <span className="text-[#CDA202] font-semibold underline">
                  Login
                </span>
              </button>
            </div>
          )}

          <p className="text-sm text-gray-600 leading-relaxed pt-4 pt-lg-5">
            By Signing up, you agree to our{" "}
            <NavLink
              to="#"
              className="text-[#CDA202] hover:text-yellow-700 underline"
            >
              Terms of Use
            </NavLink>{" "}
            and{" "}
            <NavLink
              to="#"
              className="text-[#CDA202] hover:text-yellow-700 underline"
            >
              Privacy Policy
            </NavLink>
          </p>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
